import { MotionBlurFilter } from "@pixi/filter-motion-blur";
import * as Logger from "js-logger";
import { Signal } from "signals";
import SymbolBehaviourDriver from "slotworks/components/matrix/symbol/behaviours/symbol-behaviour-driver";
import { SymbolBehaviourMethod } from "slotworks/components/matrix/symbol/behaviours/symbol-behaviour-driver";
import AnimatedSprite from "slotworks/graphics/animation/animated-sprite";
import GraphicsManager from "slotworks/graphics/graphics-manager";
import graphicsManager from "slotworks/graphics/graphics-manager";
import Layers from "slotworks/graphics/layers/layers";
import { CenterPivot } from "slotworks/graphics/pixi/group";
import { setTransformRect } from "slotworks/graphics/pixi/pixi-utils";
import SlotDefinition from "slotworks/model/slot-definition";
import SymbolDefinition from "slotworks/model/symbol-definition";
import { Services } from "slotworks/services/services";
import { SoundEvent } from "slotworks/services/sound/sound-events";
import Contract from "slotworks/utils/contracts/contract";
import Point from "slotworks/utils/geom/point";
import Rectangle from "slotworks/utils/geom/rectangle";
import { getOffsetRectangle } from "slotworks/utils/position-offset-calculator";
import AbstractSymbolBehaviour from "./behaviours/abstract-symbol-behaviour";

export default class SymbolComponent extends SymbolBehaviourDriver {

    public static maxMotionBlur: number = 0;

    /**
     * Position in matrix where {x:1, y:1} if top left
     */
    public readonly gridPosition: Point;
    public readonly landSignal: Signal;

    public symbolDefinition: SymbolDefinition;

    /**
     * The target transform of a "normal" symbol.
     * Note this is always in terms of the MatrixContent layer
     */
    protected transform: Rectangle;
    protected visible: boolean = true;
    protected motionBlurFilter: MotionBlurFilter;

    // TODO: Perhaps promote this to somewhere globally accessible
    protected masterPosition: string = "symbol_1_1";

    protected staticSprite: PIXI.Sprite;
    protected staticSpriteBlurred: PIXI.Sprite;
    protected staticBackground: PIXI.Sprite;
    protected animatedBackground: AnimatedSprite;
    protected animatedForeground: AnimatedSprite;

    protected stackOffset: number = 0;
    protected isStackPart: boolean;

    private waitingToStick: boolean;
    protected moving: boolean;
    protected blurred: boolean;

    protected staticSprites: Map<string, PIXI.Sprite>;
    protected staticSpritesBlurred: Map<string, PIXI.Sprite>;
    protected staticBackgrounds: Map<string, PIXI.Sprite>;
    protected animatedBackgrounds: Map<string, AnimatedSprite>;
    protected animatedForegrounds: Map<string, AnimatedSprite>;
    protected staticSpriteOffsets: Map<string, Rectangle>;
    protected staticBackgroundOffsets: Map<string, Rectangle>;
    protected animatedBackgroundOffsets: Map<string, Rectangle>;
    protected animatedForegroundOffsets: Map<string, Rectangle>;

    protected layerLocked: boolean;
    protected layer: Layers;
    protected container: PIXI.Container;
    protected matrixLayer: Layers;
    protected animationLayer: Layers;

    constructor(gridPosition: Point, matrixLayer: Layers, animationLayer: Layers) {
        super();

        this.matrixLayer = matrixLayer;
        this.animationLayer = animationLayer;

        this.landSignal = new Signal();

        this.gridPosition = gridPosition.clone();

        this.generateStaticSprites();

        this.setVisible(true);

        try {
            if (SymbolComponent.maxMotionBlur > 0) {
                this.motionBlurFilter = new MotionBlurFilter([0, 0], 11);
                this.staticSprite.parent.filters = [this.motionBlurFilter];
            }
        } catch (e) {
            Logger.warn("Motion blur filter not supported");
        }
    }

    public destroy() {
        this.container.parent.removeChild(this.container);
        this.staticSprites.clear();
        this.animatedBackgrounds.clear();
        this.animatedForegrounds.clear();
        this.staticBackgrounds.clear();
    }

    public static() {
        if (this.waitingToStick) {
            this.stick().execute();
        } else {
            this.executeBehaviour(SymbolBehaviourMethod.Static).execute();
        }
    }

    public land(): Contract<void> {
        Services.sound.event(SoundEvent.symbol_ID_land, this.symbolId);

        if (this.symbolDefinition.autoStick) {

            if (this.visible && !this.proxy) {
                Services.sound.event(SoundEvent.stick_symbol);
                Services.sound.event(SoundEvent.stick_symbol_ID, this.symbolId);
            }

            this.waitingToStick = true;
        }

        this.landSignal.dispatch();

        return new Contract<void>((resolve) => {
            this.executeBehaviour(SymbolBehaviourMethod.Land).then(() => {
                if (this.waitingToStick) {
                    this.stick().execute();
                }
                resolve(null);
            });
        });
    }

    public setMoving(moving: boolean) {
        this.moving = moving;
    }

    public setBlurred(blurred: boolean) {
        this.blurred = blurred;
    }

    public isMoving() {
        return this.moving;
    }

    public stick() {
        this.waitingToStick = false;
        return this.executeBehaviour(SymbolBehaviourMethod.Stick);
    }

    public expand(x: number = 0, y: number = 0): Contract<void> {
        Services.sound.event(SoundEvent.symbol_any_expand);
        Services.sound.event(SoundEvent.symbol_ID_expand, this.symbolId);
        if (y < 0) {
            Services.sound.event(SoundEvent.symbol_any_expand_up);
            Services.sound.event(SoundEvent.symbol_ID_expand_up, this.symbolId);
        } else if (y > 0) {
            Services.sound.event(SoundEvent.symbol_any_expand_down);
            Services.sound.event(SoundEvent.symbol_ID_expand_down, this.symbolId);
        }

        return this.executeBehaviour(SymbolBehaviourMethod.Expand, x, y);
    }

    public addBehaviourOfType(behaviourType: typeof AbstractSymbolBehaviour) {
        const behaviour = new behaviourType(this);
        this.addBehaviour(behaviour);
    }

    public stop(): Contract<void> {
        return this.executeBehaviour(SymbolBehaviourMethod.Stop);
    }

    public highlight(): Contract<void> {
        return this.executeBehaviour(SymbolBehaviourMethod.Highlight);
    }

    public highlightDim(): Contract<void> {
        return this.executeBehaviour(SymbolBehaviourMethod.HighlightDim);
    }

    public winDim(): Contract<void> {
        return this.executeBehaviour(SymbolBehaviourMethod.WinDim);
    }

    public cycleDim(): Contract<void> {
        return this.executeBehaviour(SymbolBehaviourMethod.CycleDim);
    }

    public win(): Contract<void> {
        return this.executeBehaviour(SymbolBehaviourMethod.Win);
    }

    public cycle(): Contract<void> {
        return this.executeBehaviour(SymbolBehaviourMethod.Cycle);
    }

    public anticipate(): Contract<void> {
        return this.executeBehaviour(SymbolBehaviourMethod.Anticipate);
    }

    public remove(): Contract<void> {
        return this.executeBehaviour(SymbolBehaviourMethod.Remove);
    }

    public setSymbol(id: string) {
        this.symbolId = id;

        this.symbolDefinition = SlotDefinition.getSymbolDefinition(this.symbolId);

        this.executeBehaviour(SymbolBehaviourMethod.SymbolChange).execute();
    }

    public symbolExists(id: string) {
        return this.staticSprites.has(id);
    }

    public refresh() {
        this.setSymbol(this.symbolId);
    }

    /**
     * Sets the position and size of the static symbol
     * Anchor point is always centered
     *
     * @param transform {Rectangle}
     */
    public setTransform(transform: Rectangle) {
        this.transform = transform;
    }

    /**
     * The current speed at which the symbol is travelling, btween 0 and 1, 1 being an entire symbol position
     */
    public setVelocity(velocity: number) {
        if (this.motionBlurFilter) {
            (this.motionBlurFilter.velocity as any) = [0, SymbolComponent.maxMotionBlur * velocity];
        }
    }

    public offsetStack(offset: number) {
        this.stackOffset = offset;
    }

    public setIsStackPart(stackPart: boolean) {
        this.isStackPart = stackPart;
        this.updateVisible();
    }

    public isPartOfStack() {
        return this.isStackPart;
    }

    public getVisible() {
        return this.visible;
    }

    /**
     * Sets visibility for the static symbol
     * @param visible {boolean}
     */
    public setVisible(visible: boolean) {
        this.visible = visible;
        this.updateVisible();
    }

    public tint(color: number) {
        const tintElementMaps = [this.staticSprites, this.staticBackgrounds, this.animatedBackgrounds, this.animatedForegrounds];

        for (const elementMap of tintElementMaps) {
            elementMap.forEach((element) => {
                if (element) {
                    element.tint = color;
                }
            });
        }
    }

    public setScale(scale: Point) {
        this.staticSprite.scale.x = scale.x;
        this.staticSprite.scale.y = scale.y;
    }

    public setMask(mask: PIXI.Sprite | PIXI.Graphics) {
        this.container.mask = mask;
    }

    public setLayer(layer: Layers) {
        if (!this.layerLocked) {
            this.layer = layer;

            this.layer.add(this.container);
        }
    }

    public moveToTop() {
        if (this.layer && this.layer === this.matrixLayer) {
            this.layer.add(this.container);
        }
    }

    public getZIndex() {
        return this.container.parent.getChildIndex(this.container);
    }

    public lockLayer() {
        this.layerLocked = true;
    }

    public getLayer() {
        return this.layer;
    }

    public updateTransform() {

        // Translate transform to local co-ordinate space
        let transform = this.transform;

        if (this.layer !== this.matrixLayer) {
            const globalPosition = this.matrixLayer.localToGlobal(transform);
            transform = this.layer.globalToLocal(globalPosition);
        }

        this.updateStatic(transform);
        this.updateStaticBackground(transform);
        this.updateAnimatedBackground(transform);
        this.updateAnimatedForeground(transform);

        // Offset stacks
        if (this.stackOffset !== 0) {
            this.staticSprite.y -= this.getStackOffsetY();
        }

        this.updateVisible();

        this.executeBehaviour(SymbolBehaviourMethod.UpdateTransform).execute();
    }

    public getTransform(): Rectangle {
        return this.transform;
    }

    /**
     * Returns a perctange of the stack this belongs to. For example if
     * this is the middle symbol of a 3-high stack, this will return 0.3.
     * If it's the bottom symbol of a 3-high stack, this will return 0.6
     */
    public getStackOffsetY(): number {
        return (1 / this.symbolDefinition.size) * this.stackOffset * this.staticSprite.height;
    }

    public getStackOffset(): number {
        return this.stackOffset;
    }

    public getMatrixLayer() {
        return this.matrixLayer;
    }

    public getAnimationLayer() {
        return this.animationLayer;
    }

    public createStaticCopy() {
        const sprite = graphicsManager.createSprite(this.symbolId);

        if (sprite) {
            const position = this.matrixLayer.getPosition(this.symbolId);
            const targetPosition = `symbol_${this.gridPosition.x}_${this.gridPosition.y}`;
            const offset = getOffsetRectangle(this.matrixLayer, "symbol_1_1", targetPosition);

            sprite.x = position.x + offset.x;
            sprite.y = position.y + offset.y;
            sprite.width = position.width;
            sprite.height = position.height;

            CenterPivot(sprite);
        }

        return sprite;
    }

    protected updateStatic(transform: Rectangle) {
        // Set correct symbol
        this.staticSprite.visible = false;
        this.staticSprite = this.staticSprites.get(this.symbolId);
        this.staticSprite.visible = true;

        // Update blurred symbol if applicable
        if (this.staticSpriteBlurred) {
            this.staticSpriteBlurred.visible = false;
        }
        this.staticSpriteBlurred = this.staticSpritesBlurred.get(this.symbolId);

        // Update basic transform
        const offset = this.staticSpriteOffsets.get(this.symbolId);

        this.staticSprite.width = transform.width * offset.width;
        this.staticSprite.height = transform.height * offset.height;

        this.staticSprite.x = offset.x + transform.x + this.staticSprite.width * 0.5;
        this.staticSprite.y = offset.y + transform.y + this.staticSprite.height * 0.5;

        // Blurred symbol should match static position
        if (this.staticSpriteBlurred) {
            setTransformRect(this.staticSpriteBlurred, this.staticSprite);
        }
    }

    protected updateStaticBackground(transform: Rectangle) {
        if (this.staticBackground) {
            this.staticBackground.visible = false;
            this.staticBackground = null;
        }
        if (this.staticBackgrounds.has(this.symbolId)) {
            this.staticBackground = this.staticBackgrounds.get(this.symbolId);

            this.staticBackground.visible = !this.proxy && !this.isStackPart;

            const bgOffset = this.staticBackgroundOffsets.get(this.symbolId);
            this.staticBackground.width = transform.width * bgOffset.width;
            this.staticBackground.height = transform.height * bgOffset.height;
            this.staticBackground.x = bgOffset.x + transform.x + this.staticBackground.width * 0.5;
            this.staticBackground.y = bgOffset.y + transform.y + this.staticBackground.height * 0.5;

        }
    }

    protected updateAnimatedBackground(transform: Rectangle) {
        if (this.animatedBackground) {
            this.animatedBackground.stop();
            this.animatedBackground.visible = false;
            this.animatedBackground = null;
        }
        if (this.animatedBackgrounds.has(this.symbolId)) {
            this.animatedBackground = this.animatedBackgrounds.get(this.symbolId);

            this.animatedBackground.visible = !this.proxy && !this.isStackPart;
            this.animatedBackground.play();

            const bgOffset = this.animatedBackgroundOffsets.get(this.symbolId);
            this.animatedBackground.width = transform.width * bgOffset.width;
            this.animatedBackground.height = transform.height * bgOffset.height;
            this.animatedBackground.x = bgOffset.x + transform.x + this.animatedBackground.width * 0.5;
            this.animatedBackground.y = bgOffset.y + transform.y + this.animatedBackground.height * 0.5;

        }
    }

    protected updateAnimatedForeground(transform: Rectangle) {
        if (this.animatedForeground) {
            this.animatedForeground.stop();
            this.animatedForeground.visible = false;
            this.animatedForeground = null;
        }
        if (this.animatedForegrounds.has(this.symbolId)) {
            this.animatedForeground = this.animatedForegrounds.get(this.symbolId);

            this.animatedForeground.visible = !this.proxy && !this.isStackPart;
            this.animatedForeground.play();

            const fgOffset = this.animatedForegroundOffsets.get(this.symbolId);
            this.animatedForeground.width = transform.width * fgOffset.width;
            this.animatedForeground.height = transform.height * fgOffset.height;
            this.animatedForeground.x = fgOffset.x + transform.x + this.animatedForeground.width * 0.5;
            this.animatedForeground.y = fgOffset.y + transform.y + this.animatedForeground.height * 0.5;

        }
    }

    protected updateVisible() {
        this.staticSprite.visible = this.visible && !this.isStackPart;

        // Use blurred static instead of regular one if moving
        if (this.staticSprite.visible && this.blurred && this.staticSpriteBlurred) {
            this.staticSpriteBlurred.visible = true;
            this.staticSprite.visible = false;
        }
    }

    protected generateStaticSprites() {

        this.container = new PIXI.Container();
        this.matrixLayer.add(this.container);

        this.layer = this.matrixLayer;

        this.staticSprites = new Map<string, PIXI.Sprite>();
        this.staticSpritesBlurred = new Map<string, PIXI.Sprite>();
        this.staticBackgrounds = new Map<string, PIXI.Sprite>();
        this.animatedBackgrounds = new Map<string, AnimatedSprite>();
        this.animatedForegrounds = new Map<string, AnimatedSprite>();
        this.staticSpriteOffsets = new Map<string, Rectangle>();
        this.staticBackgroundOffsets = new Map<string, Rectangle>();
        this.animatedBackgroundOffsets = new Map<string, Rectangle>();
        this.animatedForegroundOffsets = new Map<string, Rectangle>();

        for (const symbolDef of SlotDefinition.symbolDefinitions) {
            const sprite = this.createStaticSprite(symbolDef.id);
            this.staticSprites.set(symbolDef.id, sprite);

            const offsetRect = getOffsetRectangle(this.layer, this.masterPosition, symbolDef.id);
            this.staticSpriteOffsets.set(symbolDef.id, offsetRect);

            const animatedBackground = this.generateAnimatedBackground(symbolDef.id);
            const staticBackground = this.generateStaticBackground(symbolDef.id);

            sprite.anchor.set(0.5);
            sprite.visible = false;
            this.container.addChild(sprite);

            let staticSpriteBlurred = this.createBlurredStaticSprite(symbolDef.id);
            if (staticSpriteBlurred) {
                this.staticSpritesBlurred.set(symbolDef.id, staticSpriteBlurred);
                staticSpriteBlurred.anchor.set(0.5);
                staticSpriteBlurred.visible = false;
                this.container.addChild(staticSpriteBlurred);
            }

            const animatedForeground = this.generateAnimatedForeground(symbolDef.id);

            this.staticSprite = sprite;
            this.staticSpriteBlurred = staticSpriteBlurred;
            this.staticBackground = staticBackground;
            this.animatedBackground = animatedBackground;
            this.animatedForeground = animatedForeground;
        }
    }

    protected createStaticSprite(id: string): PIXI.Sprite {
        const texture = GraphicsManager.getTexture(id);
        return new PIXI.Sprite(texture);
    }

    protected createBlurredStaticSprite(id: string): PIXI.Sprite | null {
        if (GraphicsManager.hasTexture(id + "_blur")) {
            const textureBlurred = GraphicsManager.getTexture(id + "_blur");
            return new PIXI.Sprite(textureBlurred);
        } else {
            return null;
        }
    }

    protected generateStaticBackground(symbolId: string) {
        const staticBackground = GraphicsManager.createSprite([symbolId + "_bg"]);

        if (staticBackground) {
            this.staticBackgrounds.set(symbolId, staticBackground);
            staticBackground.visible = false;
            staticBackground.anchor.set(0.5);
            this.container.addChild(staticBackground);

            const bgOffsetRect = getOffsetRectangle(this.layer, this.masterPosition, symbolId + "_bg");
            this.staticBackgroundOffsets.set(symbolId, bgOffsetRect);
        }

        return staticBackground;
    }

    protected generateAnimatedBackground(symbolId: string) {
        const animatedBackground = GraphicsManager.createAnimation([symbolId + "_back"]);

        if (animatedBackground) {
            this.animatedBackgrounds.set(symbolId, animatedBackground);
            animatedBackground.loop = true;
            animatedBackground.stop();
            animatedBackground.visible = false;
            animatedBackground.anchor.set(0.5);
            this.container.addChild(animatedBackground);

            const bgOffsetRect = getOffsetRectangle(this.layer, this.masterPosition, symbolId + "_back");
            this.animatedBackgroundOffsets.set(symbolId, bgOffsetRect);
        }

        return animatedBackground;
    }

    protected generateAnimatedForeground(symbolId: string) {
        const animatedForeground = GraphicsManager.createAnimation([symbolId + "_fore"]);

        if (animatedForeground) {
            this.animatedForegrounds.set(symbolId, animatedForeground);
            animatedForeground.loop = true;
            animatedForeground.stop();
            animatedForeground.visible = false;
            animatedForeground.anchor.set(0.5);
            this.matrixLayer.add(animatedForeground);

            const bgOffsetRect = getOffsetRectangle(this.layer, this.masterPosition, symbolId + "_fore");
            this.animatedForegroundOffsets.set(symbolId, bgOffsetRect);
        }

        return animatedForeground;
    }
}
